import lang from '~/lib/lang';
import {InvalidArgument, ParseError} from '~/lib/exceptions';
import {WARRANTY_TERM_UNITS} from '~/lib/domain/constants';

const DATETIME = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

const DATETIME_LONG = {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
};

const MONTH_YEAR = {
  month: 'long',
  year: 'numeric',
};

/**
 * Thresholds for picking a unit to describe an elapsed span, in seconds.
 * Ordered coarsest first; the first entry whose limit the span exceeds wins.
 * `Intl.RelativeTimeFormat` formats a unit you hand it and will not choose
 * one, so the choice happens here.
 */
const SPANS = [
  ['year', 31536000],
  ['month', 2592000],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
];

// 'always' rather than 'auto': 'auto' renders the +/-1 cases as 'yesterday',
// 'last month', 'tomorrow'. Every row of the inquiry table then reads in a
// different shape, and 'last month' is vaguer than '1 month ago' when you are
// sorting leads by age.
const relative = new Intl.RelativeTimeFormat('en-US', {numeric: 'always'});

/**
 * Wraps a Date with the three display methods this codebase uses. Replaces
 * moment, which was only ever used here to format -- nothing in `src/` did
 * date arithmetic through it.
 *
 * @param {Date|string|number} value
 * @return {Object}
 */
const date = (value) => {
  let subject = value instanceof Date ? value : new Date(value);
  let valid = !isNaN(subject.getTime());

  return {
    isValid: () => valid,

    /**
     * Accepts the moment format strings this codebase actually passed, so
     * callers did not have to change. Anything else throws rather than
     * silently rendering the wrong thing.
     *
     * @param {string} pattern
     * @return {string}
     */
    format (pattern) {
      if (!valid) {
        return 'Invalid date';
      }
      switch (pattern) {
        case 'MMMM YYYY':
          return new Intl.DateTimeFormat('en-US', MONTH_YEAR).format(subject);
        case 'dddd, MMMM Do YYYY, h:mm:ss A':
          return new Intl.DateTimeFormat('en-US', DATETIME_LONG).format(subject);
        case 'MMMM D YYYY, h:mm A':
        case 'MMMM D YYYY,  h:mm A':
          return new Intl.DateTimeFormat('en-US', DATETIME).format(subject);
      }
      throw new InvalidArgument(`unsupported date format: ${pattern}`);
    },

    /**
     * @return {string} e.g. '3 hours ago', 'in 2 days'
     */
    fromNow () {
      return valid ? relative_span((subject.getTime() - Date.now()) / 1000) : 'Invalid date';
    },
  };
};

/**
 * Describes a signed span of seconds relative to now. Negative is past.
 *
 * @param {number} seconds
 * @return {string}
 */
export const relative_span = (seconds) => {
  if (typeof seconds !== 'number' || isNaN(seconds)) {
    throw new InvalidArgument('expected seconds arg to be a number');
  }
  let magnitude = Math.abs(seconds);
  for (let [unit, limit] of SPANS) {
    if (magnitude >= limit) {
      return relative.format(Math.trunc(seconds / limit), unit);
    }
  }
  return relative.format(Math.trunc(seconds), 'second');
};

export default date;

export const duration = {};

duration.UNITS = {
  'Y': 'years',
  'M': 'months',
  'D': 'days',
};

duration.tokenize = (spec) => {
  if (typeof spec !== 'string') {
    throw new InvalidArgument('expected spec arg to be a string');
  }
  let pattern = /^P(\d+)(\w+)$/;
  let tokens = spec.match(pattern);
  if (!tokens || tokens.length < 3) {
    throw new ParseError('failed to parse ISO duration spec');
  }
  let [_, length, unit] = tokens;
  return {unit, length};
};

/**
 * Renders a warranty term as entered: 'P18M' -> '18 months', 'P1Y' -> '1 year'.
 *
 * moment's `humanize` rounded instead -- it rendered 'P18M' as '2 years' and
 * 'P1Y' as 'a year'. An 18-month warranty is enterable in `WarrantyField.vue`
 * (a free-text length plus a Y/M/D unit), so that rounding misstated real
 * warranty terms to customers. Report the term the user chose.
 *
 * @param {string} spec ISO 8601 duration, e.g. 'P5Y'
 * @return {string}
 */
duration.humanize = (spec) => {
  let {unit, length} = duration.tokenize(spec);
  let plural = WARRANTY_TERM_UNITS[unit];
  if (!plural) {
    throw new InvalidArgument(`unsupported duration unit: ${unit}`);
  }
  let count = parseInt(length, 10);
  return `${count} ${count === 1 ? plural.replace(/s$/, '') : plural}`;
};

duration.spec = (unit, length) => {
  let units = duration.UNITS;
  let reducer = (ok, current) => !ok || units.hasOwnProperty(unit);
  let valid = length > 0 && lang.collection.reduce(units, reducer, true);
  return `P${length}${unit}`;
};
